<script setup lang="ts">
import { computed, ref } from 'vue';

import Modal from '@/components/base/layout/Modal.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import CustomizerCatalogSort from '@/components/customizer/CustomizerCatalogSort.vue';
import CustomizerCategoryFilterControlPanel from '@/components/customizer/CustomizerCategoryFilterControlPanel.vue';
import IngredientCatalog from '@/components/customizer/CustomizerIngredientCatalog.vue';
import CustomizerKeywordFilter from '@/components/customizer/CustomizerKeywordFilter.vue';
import CustomizerTagFilterControlPanel from '@/components/customizer/CustomizerTagFilterControlPanel.vue';
import { Category, SkuSelection, Sort, VolumetricSelection } from '@/lib/customizers/common';
import { SkuIngredient, VolumetricIngredient } from '@/lib/customizers/ingredients';

const props = withDefaults(
  defineProps<{
    activeTags: Set<string>;
    categoryTree: Category[];
    selections: (VolumetricSelection | SkuSelection | undefined)[];
    filters: string[];
    keyword?: string;
    ingredients: (VolumetricIngredient | SkuIngredient)[];
    isOpen: boolean;
    mode: 'box' | 'mix';
    maxIngredients: number;
    sort?: Sort;
    showPrice?: boolean;
    tags: Set<string>;
  }>(),
  {
    isOpen: false,
  },
);

const emit = defineEmits([
  'add-ingredient',
  'close-modal',
  'remove-ingredient',
  'toggle-category',
  'toggle-filter',
  'update:filters',
  'update:keyword',
  'update:sort',
]);

const activeFilters = computed({
  get: () => props.filters,
  set: (val) => emit('update:filters', val),
});

const activeKeyword = computed({
  get: () => props.keyword,
  set: (val) => emit('update:keyword', val),
});

const activeSort = computed({
  get: () => props.sort,
  set: (val) => emit('update:sort', val),
});

const showTagAndCatalogFilters = ref(false);

const showHideFilters = () => {
  showTagAndCatalogFilters.value = !showTagAndCatalogFilters.value;
};

const addedItemsCount = computed(() => props.selections.filter((item) => item !== null).length);

const handleAddIngredient = (productKey: string) => emit('add-ingredient', productKey);
const handleClose = () => emit('close-modal');
const handleRemoveIngredient = (productKey: string) => emit('remove-ingredient', productKey);
const toggleActiveCategoryFilter = (category: Category) => emit('toggle-category', category);
const toggleActiveFilter = (key: string) => emit('toggle-filter', key);
</script>

<template>
  <Modal
    backgroundColor="bg-nuts-stone-100"
    :hasControls="false"
    :isOpen="isOpen"
    width="overflow-hidden max-h-[90vh]"
    @handle-close="handleClose"
  >
    <template v-slot:header>
      <div class="flex items-end gap-4">
        <h3 class="text-left">Select Items</h3>
        <BaseBodyText>
          {{ addedItemsCount }} {{ mode === 'box' ? 'of 6 ' : '' }}selected
        </BaseBodyText>
      </div>
    </template>
    <template v-slot:body>
      <div class="flex flex-col h-screen sm:max-w-6xl">
        <div class="mt-5 md:flex-4">
          <div class="h-full" id="custom-tray-builder">
            <CustomizerKeywordFilter v-model="activeKeyword" class="mb-2" />
            <div
              class="p-0 mb-4 overflow-hidden bg-white rounded-b-xl"
              :class="showTagAndCatalogFilters ? 'h-60 md:h-48' : 'h-10'"
              data-test="filter-sort"
            >
              <button
                class="relative flex items-center justify-center w-full h-10 mb-0 text-base text-center text-white bg-black border-none cursor-pointer font-proxima-nova rounded-t-xl"
                :class="showTagAndCatalogFilters ? 'rounded-b-xl minus-on' : 'plus-on'"
                @click="showHideFilters"
              >
                <span class="text-sm font-semibold"
                  >Filter &amp; Sort (<span>{{ activeFilters.length }}</span
                  >)
                </span>
              </button>
              <div v-if="showTagAndCatalogFilters" class="flex flex-col h-10">
                <CustomizerTagFilterControlPanel
                  :activeFilters="activeFilters"
                  :activeTags
                  :tags
                  @toggle-filter="toggleActiveFilter"
                />
                <CustomizerCatalogSort v-model="activeSort" />
              </div>
            </div>
            <div class="flex-row -mx-4 wrapped mb-15">
              <div class="max-w-full overflow-auto scrollbar-hide">
                <CustomizerCategoryFilterControlPanel
                  class="mb-15"
                  :activeFilters="activeFilters"
                  :catalogCategoryTree="categoryTree"
                  @toggle-category="toggleActiveCategoryFilter"
                />
              </div>
            </div>
          </div>
        </div>
        <IngredientCatalog
          class="mt-4 overflow-y-auto"
          :ingredients="ingredients"
          :maxIngredients="maxIngredients"
          :mode="mode"
          :selections="selections"
          :showPrice="showPrice"
          @add-ingredient="handleAddIngredient"
          @close-modal="handleClose"
          @remove-ingredient="handleRemoveIngredient"
        />
      </div>
    </template>
  </Modal>
</template>

<style lang="scss" scoped>
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.plus-on::after {
  content: '+';
  position: absolute;
  right: 20px;
  font-size: 22px;
  font-weight: 600;
  top: 8px;
}
.minus-on::after {
  content: '-';
  position: absolute;
  right: 22px;
  font-size: 22px;
  font-weight: 600;
  top: 6px;
}
</style>
